import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// POST /api/admin/mdf/requests/:id/approve
//
// Replaces /app/admin/mdf/_actions.ts::approveRequestAction. Pessimistic
// per Session 8 pickup ("approval is the moment server-side balance recheck
// matters most") — a parallel admin could have eaten the remaining
// allocation balance in the time between the admin clicking and the
// request reaching the worker, returning 4xx; rolling that back from an
// optimistic patch would briefly flash an "approved" state.
//
// Service-side fan-out: dispatches MDF_REQUEST_APPROVED via
// notifications + posts a HubSpot Note (fire-and-forget, never blocks).
//
// Invalidates the admin queue (`mdf.requests`), the specific request
// (`mdf.request(id)`), and the parent fund detail (`mdf.fund(fundId)`).
// Caller passes `fundId` in Vars so the hook can target the right cache
// without an extra round-trip.
type Vars = { id: string; fundId: string };

export function useApproveRequest() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ id }) =>
      apiFetch<{ ok: true }>(`/admin/mdf/requests/${id}/approve`, {
        method: "POST",
      }),
    onSuccess: (_data, { id, fundId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mdf.requests() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.mdf.request(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.mdf.fund(fundId) });
      toast.success("Request approved");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to approve request");
    },
  });
}
